import { ApiError, toRecoveryAction, type RecoveryAction, type ToRecoveryOptions } from "../recovery/index.js";

/**
 * The email-OTP step shared by sign-in, sign-up and reset: code validation,
 * the 30 s resend timer and how a failed check is shown.
 *
 * - A wrong code (400/404) clears the field, keeps focus, and is retried.
 * - Anything else (network, 5xx, 429) keeps the typed code for a retry.
 * - A dead code (`otp_locked`, `otp_expired`, `otp_used`) can't be retried:
 *   it becomes a "Resend code" recovery, so the user never loops on it.
 */

export const OTP_LENGTH = 6;
export const OTP_RESEND_SECONDS = 30;

export const DEAD_OTP_CODES = new Set(["otp_locked", "otp_expired", "otp_used"]);

export interface OtpStepState {
  otpId?: string;
  /** Epoch ms after which "Resend code" is available. */
  resendAvailableAt?: number;
  /** Bumped when the OTP field must be cleared. */
  otpResetCount: number;
}

/** Digits only; null unless it's exactly OTP_LENGTH digits. */
export function normalizeOtp(code: string): string | null {
  const otp = code.replace(/\D/g, "");
  return otp.length === OTP_LENGTH ? otp : null;
}

export function otpIssued(otpId: string, now: number): Pick<OtpStepState, "otpId" | "resendAvailableAt"> {
  return { otpId, resendAvailableAt: now + OTP_RESEND_SECONDS * 1000 };
}

export function canResend(state: OtpStepState, now: number): boolean {
  return !!state.otpId && (state.resendAvailableAt ?? 0) <= now;
}

/** State patch for a failed OTP check. Clears the field for a wrong or dead code only. */
export function otpFailure(
  err: unknown,
  state: OtpStepState,
  recovery?: ToRecoveryOptions,
): { busy: false; error: RecoveryAction; otpResetCount: number } {
  const otpResetCount = state.otpResetCount + 1;
  const base = toRecoveryAction(err, recovery);
  if (err instanceof ApiError) {
    const code = err.problem?.errorCode ?? "";
    if (DEAD_OTP_CODES.has(code)) {
      return {
        busy: false,
        otpResetCount,
        error: {
          ...base,
          errorCode: code,
          messageKey: `errors.${code}`,
          action: "resend_code",
          kind: "recoverable",
          fields: ["otp"],
        },
      };
    }
    if (err.status === 400 || err.status === 404) {
      return {
        busy: false,
        otpResetCount,
        error: {
          ...base,
          errorCode: "auth_invalid_otp",
          messageKey: "errors.auth_invalid_otp",
          action: "retry",
          kind: "recoverable",
          fields: ["otp"],
        },
      };
    }
  }
  return { busy: false, otpResetCount: state.otpResetCount, error: base };
}
